import { contractTypesQueryOptions } from '@/shared/contract-types-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { formErrors, revalidateLogic, useForm, useStore } from '@eureca/ui/forms';
import { toast, utcIsoToZonedParts } from '@eureca/ui';
import { z } from 'zod';
import { APP_TIMEZONE } from '@/lib/timezone';
import { toFriendlyMessage } from '@/lib/api-error';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';
import { ProgramEditPage as ProgramEditPageComponent } from './ProgramEditPage.component';
import type { ProgramEditStep, ProgramStatus } from './ProgramEditShell';
import type { ProgramAction } from '../program-actions-menu';
import { programByIdQueryOptions } from './program-by-id-query';
import { isProgramReadOnly, mapProgramStatusKey } from './program-status';
import {
  ProgramMutationError,
  useCancelProgram,
  useClearProgramCover,
  useEndProgram,
  usePatchProgram,
  useDeleteProgram,
  useDuplicateProgram,
  usePublishProgram,
  useUploadProgramCover,
} from './program-mutations';
import { ProgramPublishDialog } from '../programs-list/ProgramPublishDialog';
import { ProgramConfirmDialog } from '../programs-list/ProgramConfirmDialog';
import type { PublishVisibility } from '../programs-list/ProgramPublishDialog';
import { PUBLISH_INCOMPLETE_JOBS_MESSAGE, usePublishReadiness } from './publish-readiness-query';
import { useProgramExportMenu } from '../program-export/use-program-export-menu';
import { recruiterEnv } from '@/config/env';
import { buildCandidateProgramUrl, copyPrivateLink, openPrivateLink } from '@/lib/candidate-links';

// `startDate`/`endDate` são instantes UTC (ISO 8601) editados em
// America/Sao_Paulo pelo DateTimePicker.
function isValidInstant(s: string): boolean {
  return !Number.isNaN(Date.parse(s));
}

/** Hoje (dia civil) em America/Sao_Paulo como `YYYY-MM-DD`. */
function todayBrt(): string {
  return utcIsoToZonedParts(new Date().toISOString(), APP_TIMEZONE).date;
}

/** Dia civil (America/Sao_Paulo) de um instante UTC, como `YYYY-MM-DD`. */
function brtDate(iso: string): string {
  return utcIsoToZonedParts(iso, APP_TIMEZONE).date;
}

const baseSchema = z.object({
  name: z.string().min(1, 'Informe o nome do programa'),
  description: z.string().min(1, 'Informe a descrição'),
  contractType: z.string().min(1, 'Selecione o tipo de contratação'),
  startDate: z
    .string()
    .min(1, 'Selecione uma data de início')
    .refine(isValidInstant, 'Selecione uma data válida'),
  endDate: z
    .string()
    .min(1, 'Selecione uma data de fim')
    .refine(isValidInstant, 'Selecione uma data válida'),
  targetRegistrations: z
    .string()
    .min(1, 'Informe a meta de inscritos')
    .regex(/^\d+$/, 'Informe um número inteiro'),
});

type FormValues = z.infer<typeof baseSchema>;

/**
 * EU-151 RN-05 — "Todas as validações da criação se aplicam igualmente na
 * edição". Adds the same date refines used by the create flow:
 *   - past-start is invalid (UX-literal: "Escolha uma data a partir de hoje");
 *   - end ≤ start is invalid (UX-literal: "Escolha uma data posterior ao
 *     início"), strict so "same-day" also blocks.
 *
 * Carve-out: past-start does NOT fire when the consultor didn't change the
 * field. Legacy programs whose inscription window already started shouldn't
 * be blocked from saving unrelated edits (description, benefits, etc.). The
 * end > start invariant is always enforced — if a legacy row violates that,
 * Core would have rejected the original create.
 */
function buildEditSchema(initialStartDate: string) {
  const today = todayBrt();
  return baseSchema.superRefine((val, ctx) => {
    if (
      val.startDate !== initialStartDate &&
      val.startDate &&
      isValidInstant(val.startDate) &&
      brtDate(val.startDate) < today
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Escolha uma data a partir de hoje',
      });
    }
    if (
      val.startDate &&
      val.endDate &&
      isValidInstant(val.startDate) &&
      isValidInstant(val.endDate) &&
      new Date(val.endDate).getTime() <= new Date(val.startDate).getTime()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Escolha uma data posterior ao início',
      });
    }
  });
}

const MAX_COVER_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_COVER_TYPES = ['image/jpeg', 'image/png'];

/**
 * Translate the info-tab form values into the PATCH body. Empty strings on
 * `contractType` / `targetRegistrations` map to `null` so the server clears
 * the column. `targetRegistrations` is parsed as an int — non-numeric input
 * is rejected by the form's Zod schema before this runs.
 */
function buildInfoPatch(values: FormValues) {
  const subscriberTarget =
    values.targetRegistrations === '' ? null : Number(values.targetRegistrations);
  return {
    name: values.name,
    description: values.description || null,
    startApplying: values.startDate || null,
    endApplying: values.endDate || null,
    workCondition: {
      contractTypeKey: values.contractType || null,
      subscriberTarget: Number.isFinite(subscriberTarget) ? subscriberTarget : null,
    },
  };
}

type ActiveDialog = 'publish' | 'end' | 'cancel' | null;

export default function ProgramEditPage() {
  // EU-709 RN-01 — os tipos vêm do catálogo (origem única, espelhada com o
  // `Tipo de vaga`). Enquanto carrega, a lista fica vazia: melhor nenhum chip
  // do que quatro chips desatualizados escritos no cliente.
  const contractTypesQuery = useQuery(contractTypesQueryOptions());
  const contractTypeOptions = useMemo(
    () => (contractTypesQuery.data ?? []).map((t) => ({ value: t.key, label: t.label })),
    [contractTypesQuery.data]
  );

  const navigate = useNavigate();
  const { programId } = useParams({ from: '/programas/$programId/editar' });
  const { copy: isCopy = false } = useSearch({
    from: '/programas/$programId/editar',
  });

  const { data: program } = useSuspenseQuery(programByIdQueryOptions(programId));
  const patchProgram = usePatchProgram(programId);
  const deleteProgram = useDeleteProgram(programId);
  const duplicateProgram = useDuplicateProgram();
  const uploadCover = useUploadProgramCover();
  const clearCover = useClearProgramCover();

  const status: ProgramStatus = mapProgramStatusKey(program.statusKey);
  // EU-570 — Encerrado/Cancelado abrem em modo somente visualização. A cópia
  // (rascunho duplicado) nunca é read-only.
  const isReadOnly = !isCopy && isProgramReadOnly(status);

  // The backend stores the " (Cópia)" suffix on duplicate, so use the server name verbatim.
  const baseName = program.name;
  // Hydrate every form field from the rich detail payload. `contractType` and
  // `targetRegistrations` live on the joined `programWorkCondition` row — null
  // when the program never had a work condition saved.
  const initialValues: FormValues = {
    name: baseName,
    description: program.description ?? '',
    contractType: program.workCondition?.contractType?.key ?? '',
    // Full UTC instant (the DateTimePicker renders it in America/Sao_Paulo).
    startDate: typeof program.startApplying === 'string' ? program.startApplying : '',
    endDate: typeof program.endApplying === 'string' ? program.endApplying : '',
    targetRegistrations:
      program.workCondition?.subscriberTarget != null
        ? String(program.workCondition.subscriberTarget)
        : '',
  };

  // Pending upload: holds the `File` the user just picked but hasn't yet
  // saved. Cleared after a successful upload so we don't re-PUT on the next
  // save. `coverFileName` mirrors the previous prop contract for the pure
  // component — initialized to a reminder string when the program already
  // has a cover stored, so the user sees that one is in place.
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);
  // Local "user clicked X on the existing cover" marker. Frontend-only until
  // Core exposes an endpoint to clear `program.coverKey` (today's `PATCH
  // /programs/:id/cover` requires `coverKey: string` min(1), and there's no
  // `DELETE /programs/:id/cover`). When true, the preview hides immediately;
  // on save we leave the saved cover untouched (server-side no-op) and the
  // local flag resets on the next page load. TODO EU-XXX — wire the actual
  // removal once Core lands the endpoint.
  const [wantsCoverCleared, setWantsCoverCleared] = useState(false);
  const [coverFileName, setCoverFileName] = useState<string | null>(
    program.coverUrl ? 'Capa atual carregada' : null
  );
  const [coverError, setCoverError] = useState<string | null>(null);

  // Preview URL precedence: just-picked file > "marked for removal" (hide
  // entirely) > saved program cover. Object URL is revoked on unmount + on
  // every coverFile change so we don't leak blobs.
  const [pendingCoverPreviewUrl, setPendingCoverPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingCoverFile) {
      setPendingCoverPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingCoverFile);
    setPendingCoverPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingCoverFile]);
  const coverPreviewUrl = pendingCoverPreviewUrl ?? (wantsCoverCleared ? null : program.coverUrl);
  const [isStepsOpen, setIsStepsOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [publishVisibility, setPublishVisibility] = useState<PublishVisibility>('public');
  // EU-450 — pre-check on publish-dialog open: blocks Publicar + lists the
  // incomplete jobs (linked to /vagas/:id/editar in a new tab) while any
  // linked vaga misses required sections. Core's PATCH gate is the authority.
  const publishReadiness = usePublishReadiness(programId, activeDialog === 'publish');
  const publishBlocked = activeDialog === 'publish' && publishReadiness.data?.ready === false;
  const publishError = publishBlocked ? PUBLISH_INCOMPLETE_JOBS_MESSAGE : undefined;
  const publishIncompleteJobs = publishBlocked
    ? publishReadiness.data?.incompleteJobs.map(({ id, name }) => ({ id, name }))
    : undefined;
  const isCheckingPublishReadiness = activeDialog === 'publish' && publishReadiness.isLoading;
  const publishProgramMutation = usePublishProgram();
  const { exportTypes, handleExportAction } = useProgramExportMenu(programId);
  const endProgramMutation = useEndProgram();
  const cancelProgramMutation = useCancelProgram();
  // RN-03 inline error (EU-151 RN-05 — same validations as create on edit).
  // Set when the BFF returns 409 `duplicate_program_name` from the PATCH; the
  // SPA renders the AC-literal message next to the Nome field instead of a
  // toast, matching the create-flow UX. Cleared when the consultor retypes
  // the name (see `onFieldChange` wrapper at the component prop site).
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);

  // Stable schema bound to the program's saved startDate — see
  // `buildEditSchema` for the past-start carve-out.
  const schema = useMemo(() => buildEditSchema(initialValues.startDate), [initialValues.startDate]);

  const form = useForm({
    defaultValues: initialValues,
    // Validate when the field loses focus, then keep validating on every
    // change. This way the user sees errors as soon as they tab out of an
    // empty/invalid field — no need to wait for the submit click.
    validationLogic: revalidateLogic({ mode: 'blur', modeAfterSubmission: 'change' }),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      if (isReadOnly) return; // EU-570 — nunca persiste em modo visualização.
      if (isCopy && !isDirty) {
        toast('Altere a cópia antes de salvar', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
        return;
      }
      await maybeUploadCover();
      const patched = await runPatchWithDupGuard(buildInfoPatch(value));
      if (!patched) return; // RN-03 fired — error shown inline, stay on the page.
      toast('Programa editado', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      await navigate({
        to: '/programas/$programId/editar/beneficios',
        params: { programId },
        search: isCopy ? { copy: true } : {},
      });
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting =
    useStore(form.store, (state) => state.isSubmitting) || patchProgram.isPending;
  const isFormDirty = useStore(form.store, (state) => state.isDirty);
  // A pending cover file OR a "remove the saved cover" request both count as
  // cover-side mutations that should let the consultor save. The placeholder
  // filename for an unchanged existing cover does NOT.
  const isDirty = isFormDirty || pendingCoverFile !== null || wantsCoverCleared;
  const fieldErrors = formErrors(form);
  const errors: Record<string, string> = { ...fieldErrors };
  if (coverError) {
    errors.cover = coverError;
  }
  // Surface RN-03 inline next to the name field, but only if zod isn't
  // already complaining about it (zod errors take precedence — e.g., empty
  // name should show "Informe o nome" not the dup error).
  if (duplicateNameError && !errors.name) errors.name = duplicateNameError;

  function handleCoverChange(file: File | null) {
    if (!file) {
      // X click semantics: 1st click on a stack of [pending file → existing
      // cover] only clears the top layer (cancels the new pick, reveals the
      // original). 2nd click marks the original for removal so the preview
      // disappears. Each click peels one layer off whatever the user sees.
      if (pendingCoverFile) {
        setPendingCoverFile(null);
        // Roll filename back to whatever the saved cover dictates (or null).
        setCoverFileName(program.coverUrl && !wantsCoverCleared ? 'Capa atual carregada' : null);
      } else if (program.coverUrl && !wantsCoverCleared) {
        setWantsCoverCleared(true);
        setCoverFileName(null);
      }
      setCoverError(null);
      return;
    }
    if (!ALLOWED_COVER_TYPES.includes(file.type)) {
      setCoverError('Envie um arquivo .JPG ou .PNG');
      return;
    }
    if (file.size > MAX_COVER_SIZE_BYTES) {
      setCoverError('O arquivo deve ter no máximo 5 MB');
      return;
    }
    setCoverError(null);
    setPendingCoverFile(file);
    setCoverFileName(file.name);
    // Picking a new file is an implicit "I want a cover" — un-mark the
    // removal flag so the new pick takes precedence on save.
    setWantsCoverCleared(false);
  }

  /**
   * Persist whatever the consultor did to the cover before saving the rest
   * of the form. Three branches, mutually exclusive in practice:
   *   - A new file picked → upload (replaces the previous cover in S3 + Core);
   *   - Cover marked for removal AND nothing new picked → DELETE the cover
   *     (BFF writes the sentinel to Core + deletes the S3 object);
   *   - No cover-side change → no-op.
   *
   * Picking a new file always wins over a pending removal because the SPA
   * resets `wantsCoverCleared` when `handleCoverChange(file)` runs.
   */
  async function maybeUploadCover() {
    if (pendingCoverFile) {
      await uploadCover.mutateAsync({ programId, file: pendingCoverFile });
      setPendingCoverFile(null);
      return;
    }
    if (wantsCoverCleared) {
      await clearCover.mutateAsync(programId);
      setWantsCoverCleared(false);
    }
  }

  function handleStepChange(step: ProgramEditStep) {
    setIsStepsOpen(false);
    const search = isCopy ? { copy: true } : {};
    if (step === 'benefits') {
      void navigate({
        to: '/programas/$programId/editar/beneficios',
        params: { programId },
        search,
      });
      return;
    }
    if (step === 'faqs') {
      void navigate({
        to: '/programas/$programId/editar/perguntas-frequentes',
        params: { programId },
        search,
      });
    }
  }

  function handleBack() {
    void navigate({ to: '/programas' });
  }

  async function handleSaveChanges() {
    if (isReadOnly) return; // EU-570 — botão nem renderiza, guard defensivo.
    try {
      await maybeUploadCover();
      const patched = await runPatchWithDupGuard(buildInfoPatch(values));
      if (!patched) return; // RN-03 fired — error shown inline next to Nome.
      toast('Programa editado', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    } catch (err) {
      // The duplicate active-program-name 409 is handled inline by
      // `runPatchWithDupGuard`; anything reaching here is another failure,
      // shown as a friendly, non-technical message (EU-403 RN-01/RN-02).
      const message = toFriendlyMessage(err);
      toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }

  /**
   * Wrap `patchProgram.mutateAsync` so the BFF's RN-03 409 (duplicate name
   * on edit, including the "Salvar alterações" of a copy) surfaces inline
   * next to the Nome field instead of bubbling up as an unhandled rejection.
   * Returns the patched program on success, `null` when the dup gate fires
   * (so callers stop the rest of the save chain). Any other error rethrows
   * up to `handleSaveChanges`'s catch — which toasts the pt-BR phrase the
   * BFF gateway translation produced.
   */
  async function runPatchWithDupGuard(patchBody: ReturnType<typeof buildInfoPatch>) {
    try {
      return await patchProgram.mutateAsync(patchBody);
    } catch (err) {
      if (err instanceof ProgramMutationError && err.code === 'duplicate_program_name') {
        setDuplicateNameError(
          'Esse nome já está em uso em um programa ativo. Crie um diferente, por favor!'
        );
        return null;
      }
      throw err;
    }
  }

  async function handleDiscardCopy() {
    await deleteProgram.mutateAsync();
    void navigate({ to: '/programas' });
  }

  function handleSubmit() {
    if (isReadOnly) {
      // EU-570 — "Avançar" fica disponível apenas para navegação.
      void navigate({
        to: '/programas/$programId/editar/beneficios',
        params: { programId },
        search: {},
      });
      return;
    }
    if (isCopy && !isDirty) {
      toast('Altere a cópia antes de salvar', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      return;
    }
    void form.handleSubmit();
  }

  function handleHeaderAction(action: ProgramAction) {
    // EU-561 + EU-654 — "Extrair bases" também no header do editor: baixa
    // direto, com o toast de 3 estados (RN-06). Mesmo hook da listagem.
    if (handleExportAction(action)) {
      return;
    }
    if (action === 'discard') {
      void handleDiscardCopy();
      return;
    }
    if (action === 'publish') {
      setPublishVisibility('public');
      setActiveDialog('publish');
      return;
    }
    if (action === 'make-public') {
      // EU-522 RN-01/RN-02 — "Tornar público" no header do edit de um programa
      // "Ativo e priv.": troca a visibilidade direto, sem o diálogo de
      // publicação (o programa já está publicado). Inverso de "Privar".
      publishProgramMutation.mutate(
        { programId, visibility: 'public' },
        {
          onSuccess: () => {
            toast('Programa público', { action: { label: 'Fechar', onClick: () => undefined } });
          },
          onError: (err: unknown) => {
            const message =
              err instanceof ProgramMutationError && err.code === 'duplicate_program_name'
                ? err.message
                : toFriendlyMessage(err);
            toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
          },
        }
      );
      return;
    }
    if (action === 'end') {
      setActiveDialog('end');
      return;
    }
    if (action === 'cancel') {
      setActiveDialog('cancel');
      return;
    }
    if (action === 'edit') {
      // Already on the edit page — no-op.
      return;
    }
    if (action === 'open-private-link') {
      openPrivateLink(buildCandidateProgramUrl(recruiterEnv.candidateAppUrl, programId));
      return;
    }
    if (action === 'copy-private-link') {
      void copyPrivateLink(
        buildCandidateProgramUrl(recruiterEnv.candidateAppUrl, programId),
        'Link privado do programa'
      );
      return;
    }
    if (action === 'duplicate') {
      void duplicateProgram
        .mutateAsync(programId)
        .then((newProgram) => {
          void navigate({
            to: '/programas/$programId/editar',
            params: { programId: newProgram.id },
            search: { copy: true },
          });
        })
        .catch((err: unknown) => {
          // Surface the duplicate active-program-name 409 canonical wording;
          // every other failure gets a friendly message (EU-403 RN-01/RN-02).
          const message =
            err instanceof ProgramMutationError && err.code === 'duplicate_program_name'
              ? err.message
              : toFriendlyMessage(err);
          toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
        });
    }
    // make-private — EU-149-followup.
  }

  function handleDialogOpenChange(open: boolean) {
    if (!open) setActiveDialog(null);
  }

  async function handleConfirmPublish() {
    try {
      await publishProgramMutation.mutateAsync({ programId, visibility: publishVisibility });
      setActiveDialog(null);
      toast('Programa publicado', { action: { label: 'Fechar', onClick: () => undefined } });
    } catch (err) {
      // Surface the canonical wording of typed 4xx codes (duplicate name 409;
      // EU-450 incomplete-jobs 422 — race where a vaga regressed between the
      // dialog pre-check and the PATCH); every other failure gets a friendly
      // message (EU-403 RN-01/RN-02).
      const message =
        err instanceof ProgramMutationError && err.code !== null
          ? err.message
          : toFriendlyMessage(err);
      setActiveDialog(null);
      toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }
  async function handleConfirmEnd() {
    setActiveDialog(null);
    try {
      await endProgramMutation.mutateAsync(programId);
      toast('Programa encerrado', { action: { label: 'Fechar', onClick: () => undefined } });
      // The program is no longer editable once encerrado — leave the form.
      await navigate({ to: '/programas' });
    } catch {
      toast('Não foi possível encerrar o programa', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    }
  }
  async function handleConfirmCancel() {
    setActiveDialog(null);
    try {
      await cancelProgramMutation.mutateAsync(programId);
      toast('Programa cancelado', { action: { label: 'Fechar', onClick: () => undefined } });
      // The program is no longer editable once cancelado — leave the form.
      await navigate({ to: '/programas' });
    } catch {
      toast('Não foi possível cancelar o programa', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    }
  }

  // EU-570 — programas Cancelados (e Encerrados) abrem o próprio fluxo de
  // edição em modo somente visualização (campos disabled + tooltip "Edição
  // bloqueada") em vez do antigo aviso bloqueante. O BFF segue rejeitando
  // escrita server-side (409 program_cancelled), então o gate não é
  // contornável.
  return (
    <>
      <ProgramEditPageComponent
        status={status}
        brand={<WorkspaceBrand companyId={program.company.id} />}
        companyName={program.company.name}
        values={values}
        errors={errors}
        contractTypeOptions={contractTypeOptions}
        coverFileName={coverFileName}
        coverPreviewUrl={coverPreviewUrl}
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        readOnly={isReadOnly}
        isCopy={isCopy}
        onDiscardCopy={() => void handleDiscardCopy()}
        progress={10}
        currentStep={1}
        totalSteps={3}
        isStepsOpen={isStepsOpen}
        onStepsOpenChange={setIsStepsOpen}
        onStepChange={handleStepChange}
        onFieldChange={(field, value) => {
          if (field === 'name' && duplicateNameError) setDuplicateNameError(null);
          form.setFieldValue(field, value);
        }}
        onCoverChange={handleCoverChange}
        onBack={handleBack}
        onSaveChanges={() => void handleSaveChanges()}
        onSubmit={handleSubmit}
        onHeaderAction={handleHeaderAction}
        exportTypes={exportTypes}
      />
      <ProgramPublishDialog
        open={activeDialog === 'publish'}
        onOpenChange={handleDialogOpenChange}
        visibility={publishVisibility}
        onVisibilityChange={setPublishVisibility}
        onConfirm={() => void handleConfirmPublish()}
        error={publishError}
        incompleteJobs={publishIncompleteJobs}
        isCheckingReadiness={isCheckingPublishReadiness}
      />
      <ProgramConfirmDialog
        open={activeDialog === 'end'}
        onOpenChange={handleDialogOpenChange}
        title="Encerrar"
        description="Deseja encerrar essa oportunidade? Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Encerrar"
        onConfirm={handleConfirmEnd}
      />
      <ProgramConfirmDialog
        open={activeDialog === 'cancel'}
        onOpenChange={handleDialogOpenChange}
        title="Cancelar"
        description="Deseja cancelar essa oportunidade? Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Fazer cancelamento"
        onConfirm={handleConfirmCancel}
      />
    </>
  );
}
